import React, { Fragment, useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import SparepartImportDeleteForm from "./SparepartImportDeleteForm";
import SparepartImportUpdateForm from "./SparepartImportUpdateForm";
import { getSparepartImports } from "../../../actions/sparepartImports";
import SparepartImportCreateForm from "./SparepartImportCreateForm";
import SparepartImportItem from "./SparepartImportItem";
import { Plus, Filter } from "react-feather";
import TableSpinner from "../../layout/TableSpinner";
import CustomPaginationWithManyPages from "../../layout/CustomPaginationWithManyPages";
import Select from 'react-select';
import { usePermission } from '../../../hooks/usePermission';
import { getSparepartOptions, getWarehouseOptions } from '../../../actions/spare_part';
import { getSupplierOptions } from '../../../actions/supplier';
import FilterByDateRange from "../../layout/FilterByDateRange";
import FilterWithTrashed from "../../filters/FilterWithTrashed";
import FilterOnlyTrashed from "../../filters/FilterOnlyTrashed";
import { Button, Modal } from 'react-bootstrap';
import SearchForm from "../../layout/SearchForm";
import useActiveFirm from '../../../hooks/useActiveFirm';

// Create a DeletedSparepartImportsFilterModal component
const DeletedSparepartImportsFilterModal = ({ show, handleClose, getPageItems }) => {
    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>O'chirilgan ehtiyot qism importlar filtrlari</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <h6 className="mb-3">O'chirilgan ehtiyot qism importlar bilan ishlash:</h6>
                    <div className="mb-3">
                        <FilterWithTrashed getPageItems={getPageItems} />
                        <small className="text-muted d-block mt-1">
                            Barcha importlarni o'chirilganlar bilan birga ko'rsatish
                        </small>
                    </div>
                    
                    <div className="mb-3">
                        <FilterOnlyTrashed getPageItems={getPageItems} />
                        <small className="text-muted d-block mt-1">
                            Faqat o'chirilgan importlarni ko'rsatish
                        </small>
                    </div>
                </div>
                
                <div className="alert alert-info">
                    <small>
                        <strong>Eslatma:</strong> Bu filtrlar bir-birini bekor qiladi. Agar ikkala filtr ham yoqilgan bo'lsa, oxirgi tanlangan filtr ishlatiladi.
                    </small>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Yopish
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const SparepartImports = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { userCan } = usePermission();
    const { firmId, firmName } = useActiveFirm();
    const { sparepartImport } = useSelector(state => state);
    const { supplier } = useSelector(state => state);
    const { spare_part } = useSelector(state => state);
    const { 
        sparepartImports, 
        loading, 
        numberOfPages, 
        currentPage,
        total_sparepart_imports
    } = sparepartImport;
    const { suppliers_DATA } = supplier;
    const { spare_part_options } = spare_part;

    const [showCreate, setShowCreate] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedSparepart, setSelectedSparepart] = useState(null);
    
    const [showDeletedFilters, setShowDeletedFilters] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    const lastFetchedFirmIdRef = useRef(null);
    const hasFetchedSuppliersRef = useRef(false);
    const lastSparepartFirmIdRef = useRef(null);

    // Get URL parameters to check for trashed filters
    const params = new URLSearchParams(window.location.search);
    const withTrashed = params.get('with_trashed') === 'true';
    const onlyTrashed = params.get('only_trashed') === 'true';
    const hasDeletedFilters = withTrashed || onlyTrashed;

    // Initialize filters from URL parameters when options become available
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        
        // Initialize supplier filter from URL
        const supplierId = urlParams.get('supplier_id');
        if (supplierId && suppliers_DATA && suppliers_DATA.length > 0) {
            const supplier = suppliers_DATA.find(s => s.id.toString() === supplierId);
            if (supplier) {
                setSelectedSupplier({ value: supplier.id, label: supplier.name });
            }
        } else if (!supplierId) {
            setSelectedSupplier(null);
        }
    }, [location.search, suppliers_DATA]);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        
        // Initialize warehouse filter from URL (only if we have warehouse options)
        const warehouseId = urlParams.get('warehouse_id');
        if (warehouseId && spare_part.warehouse_options && spare_part.warehouse_options.length > 0) {
            const warehouse = spare_part.warehouse_options.find(w => w.id.toString() === warehouseId);
            // Only set warehouse if it exists and belongs to the active firm
            if (warehouse && (!firmId || warehouse.firm_id === parseInt(firmId))) {
                setSelectedWarehouse({ value: warehouse.id, label: warehouse.name });
            } else {
                // Warehouse doesn't belong to active firm, clear it
                setSelectedWarehouse(null);
            }
        } else if (!warehouseId) {
            setSelectedWarehouse(null);
        }
    }, [location.search, spare_part.warehouse_options, firmId]);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        
        // Initialize sparepart filter from URL
        const sparepartId = urlParams.get('sparepart_id');
        if (sparepartId && spare_part_options && spare_part_options.length > 0) {
            const sparepart = spare_part_options.find(sp => sp.id.toString() === sparepartId);
            if (sparepart) {
                setSelectedSparepart({ value: sparepart.id, label: sparepart.name });
            }
        } else if (!sparepartId) {
            setSelectedSparepart(null);
        }
    }, [location.search, spare_part_options]);

    const handleCreateClose = () => setShowCreate(false);
    const handleCreateShow = () => setShowCreate(true);
    
    const handleDeletedFiltersClose = () => setShowDeletedFilters(false);
    const handleDeletedFiltersShow = () => setShowDeletedFilters(true);
    
    const showLoader = () => setIsLoading(true);
    const hideLoader = () => setIsLoading(false);

    // Wrapper function to ensure firm_id is always included when calling getSparepartImports
    const getSparepartImportsWithFirm = useCallback((page, params = {}) => {
        const filters = { ...params };
        if (firmId) {
            filters.firm_id = firmId;
        }
        // Clean up undefined/null values
        Object.keys(filters).forEach(key => {
            if (filters[key] === undefined || filters[key] === null || filters[key] === '' || filters[key] === 'undefined' || filters[key] === 'null') {
                delete filters[key];
            }
        });
        return getSparepartImports(page, filters);
    }, [firmId]);

    // Fetch suppliers once
    useEffect(() => {
        if (!hasFetchedSuppliersRef.current) {
            hasFetchedSuppliersRef.current = true;
            dispatch(getSupplierOptions());
        }
    }, [dispatch]);

    // Fetch sparepart and warehouse options when firm changes
    useEffect(() => {
        if (firmId && lastSparepartFirmIdRef.current !== firmId) {
            lastSparepartFirmIdRef.current = firmId;
            dispatch(getSparepartOptions({ firm_id: firmId }));
            dispatch(getWarehouseOptions({ firm_id: firmId, type: 'part' }));
        }
    }, [firmId, dispatch]);

    // Fetch initial data when component mounts or firm changes
    useEffect(() => {
        if (!firmId || lastFetchedFirmIdRef.current === firmId) return;
        
        lastFetchedFirmIdRef.current = firmId;
        const urlParams = new URLSearchParams(location.search);
        const initialFilters = {};
        
        // Get filters from URL only
        for (const [key, value] of urlParams.entries()) {
            if (value && value !== 'undefined' && value !== 'null') {
                initialFilters[key] = value;
            }
        }
        
        // Add per_page parameter
        initialFilters.per_page = 10;
        
        dispatch(getSparepartImportsWithFirm(1, initialFilters));
    }, [dispatch, firmId, getSparepartImportsWithFirm]);

    // Reset warehouse selection if it's no longer valid for the current firm
    useEffect(() => {
        if (selectedWarehouse && spare_part.warehouse_options && spare_part.warehouse_options.length > 0) {
            // Check if the selected warehouse is still valid for the active firm
            const isValidWarehouse = spare_part.warehouse_options.some(w => 
                w.id === selectedWarehouse.value && 
                (!firmId || w.firm_id === parseInt(firmId))
            );
            
            if (!isValidWarehouse) {
                setSelectedWarehouse(null);
            }
        }
    }, [firmId, spare_part.warehouse_options, selectedWarehouse]);

    // Reset local loading state when Redux loading state changes
    useEffect(() => {
        if (!loading) {
            setIsLoading(false);
        }
    }, [loading]);



            if (!userCan('view sparepart imports')) {
        return <div>Siz ehtiyot qism importlarni ko'rish uchun ruxsatga ega emassiz.</div>;
    }

    const supplierOptions = suppliers_DATA ? suppliers_DATA.map(supplier => ({
        value: supplier.id,
        label: supplier.name
    })) : [];

    // Filter warehouses based on active firm
    const warehouseOptions = spare_part.warehouse_options ? 
        spare_part.warehouse_options
            .filter(warehouse => !firmId || warehouse.firm_id === parseInt(firmId))
            .map(warehouse => ({
                value: warehouse.id,
                label: warehouse.name
            })) : [];

    const sparepartOptions = spare_part_options ? spare_part_options.map(sparepart => ({
        value: sparepart.id,
        label: sparepart.name
    })) : [];

    const handleSupplierChange = useCallback((selected) => {
        setSelectedSupplier(selected);
        
        const params = new URLSearchParams(location.search);
        const filters = {};
        
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }
        
        if (selected) {
            filters.supplier_id = selected.value;
        } else {
            delete filters.supplier_id;
        }
        filters.per_page = 10;
        
        // Update URL parameters
        const url = new URL(window.location);
        if (selected) {
            url.searchParams.set('supplier_id', selected.value);
        } else {
            url.searchParams.delete('supplier_id');
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        dispatch(getSparepartImportsWithFirm(1, filters));
    }, [dispatch, location.search, getSparepartImportsWithFirm]);

    const handleWarehouseChange = useCallback((selected) => {
        setSelectedWarehouse(selected);
        
        const params = new URLSearchParams(location.search);
        const filters = {};
        
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }
        
        if (selected) {
            filters.warehouse_id = selected.value;
        } else {
            delete filters.warehouse_id;
        }
        filters.per_page = 10;
        
        // Update URL parameters
        const url = new URL(window.location);
        if (selected) {
            url.searchParams.set('warehouse_id', selected.value);
        } else {
            url.searchParams.delete('warehouse_id');
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        dispatch(getSparepartImportsWithFirm(1, filters));
    }, [dispatch, location.search, getSparepartImportsWithFirm]);

    const handleSparepartChange = useCallback((selected) => {
        setSelectedSparepart(selected);
        
        const params = new URLSearchParams(location.search);
        const filters = {};
        
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }
        
        if (selected) {
            filters.sparepart_id = selected.value;
        } else {
            delete filters.sparepart_id;
        }
        filters.per_page = 10;
        
        // Update URL parameters
        const url = new URL(window.location);
        if (selected) {
            url.searchParams.set('sparepart_id', selected.value);
        } else {
            url.searchParams.delete('sparepart_id');
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        dispatch(getSparepartImportsWithFirm(1, filters));
    }, [dispatch, location.search, getSparepartImportsWithFirm]);

    // Check if we have pagination data and total items
    const hasPaginationData = numberOfPages !== undefined && currentPage !== undefined;
    const hasItems = sparepartImports && sparepartImports.length > 0;
    const shouldShowPagination = hasPaginationData && (numberOfPages > 1 || total_sparepart_imports > 10);

    return (
        <Fragment>
            <div className="container-fluid p-0">
                {/* Header */}
                <div className="row mb-3">
                    <div className="col-md-6">
                        <h4 className="mb-0">
                            Ehtiyot Qism Importlar
                            {total_sparepart_imports !== undefined && (
                                <span className="badge bg-secondary ms-2">{total_sparepart_imports}</span>
                            )}
                            {firmName && <span className="text-muted ms-2" style={{fontSize: '14px', fontWeight: 'normal'}}>— {firmName}</span>}
                        </h4>
                        <small className="text-muted">
                            Ehtiyot qismlar import tarixi va boshqaruvi
                        </small>
                    </div>
                    <div className="col-md-6 text-end">
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="me-2"
                            onClick={handleDeletedFiltersShow}
                        >
                            <Filter size={16} className="me-1" />
                            O'chirilganlar
                            {hasDeletedFilters && <span className="badge bg-warning ms-1">1</span>}
                        </Button>
                        
                        {userCan('create sparepart imports') && (
                            <Button 
                                variant="primary" 
                                size="sm"
                                onClick={handleCreateShow}
                            >
                                <Plus size={16} className="me-1" />
                                Yangi Import
                            </Button>
                        )}
                    </div>
                </div>

                {/* Filters */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <Select
                            value={selectedWarehouse}
                            onChange={handleWarehouseChange}
                            options={warehouseOptions}
                            placeholder={
                                firmId
                                    ? warehouseOptions.length === 0 
                                        ? "Bu firma uchun omborlar yo'q" 
                                        : "Omborni tanlang"
                                    : "Firma tanlang"
                            }
                            isClearable
                            isDisabled={!firmId}
                        />
                    </div>
                    <div className="col-md-4">
                        <Select
                            value={selectedSupplier}
                            onChange={handleSupplierChange}
                            options={supplierOptions}
                            placeholder="Barcha ta'minotchilar"
                            isClearable
                        />
                    </div>
                    <div className="col-md-4">
                        <Select
                            value={selectedSparepart}
                            onChange={handleSparepartChange}
                            options={sparepartOptions}
                            placeholder="Barcha ehtiyot qismlar"
                            isClearable
                        />
                    </div>
                </div>

                {/* Search and Date Range Filter */}
                <div className="row mb-3">
                    <div className="col-md-6">
                        <SearchForm 
                            getPageItems={getSparepartImportsWithFirm}
                            placeholder="Import raqami yoki izoh..."
                        />
                    </div>
                    <div className="col-md-6">
                        <FilterByDateRange 
                            getPageItems={getSparepartImportsWithFirm}
                            showLoader={showLoader}
                            hideLoader={hideLoader}
                        />
                    </div>
                </div>

                {/* Imports Table */}
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body p-0">
                                {(loading || isLoading) && <TableSpinner />}
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col">#</th>
                                                <th scope="col">Import raqami</th>
                                                <th scope="col">Ombor</th>
                                                <th scope="col">Ehtiyot qism</th>
                                                <th scope="col">Ta'minotchi</th>
                                                <th scope="col">Transport raqami</th>
                                                <th scope="col">Jami summa</th>
                                                <th scope="col">Sana</th>
                                                <th scope="col">Amallar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hasItems ? (
                                                sparepartImports.map((item, index) => (
                                                    <SparepartImportItem
                                                        key={item.id}
                                                        sparepartImport={item}
                                                        number={(currentPage - 1) * 10 + index + 1}
                                                    />
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {loading || isLoading ? 'Ma\'lumotlar yuklanmoqda...' : 'Ehtiyot qism importlar topilmadi'}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pagination */}
                {shouldShowPagination && (
                    <div className="row mt-3">
                        <div className="col-12">
                            <CustomPaginationWithManyPages
                                numberOfPages={numberOfPages}
                                getPageItems={getSparepartImportsWithFirm}
                                current_page={currentPage}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {userCan('create sparepart imports') && (
                <SparepartImportCreateForm
                    show={showCreate}
                    handleClose={handleCreateClose}
                />
            )}

            {userCan('edit sparepart imports') && (
                <SparepartImportUpdateForm />
            )}

            {userCan('delete sparepart imports') && (
                <SparepartImportDeleteForm />
            )}

            <DeletedSparepartImportsFilterModal
                show={showDeletedFilters}
                handleClose={handleDeletedFiltersClose}
                getPageItems={getSparepartImportsWithFirm}
            />
        </Fragment>
    );
};

export default SparepartImports; 